import React from "react";
import type { TaktNodeMapNode, TaktNodeMapping } from "../../../../../models";
import "./LeafRow.css";

type Dim = keyof TaktNodeMapping;

interface Props {
  node: TaktNodeMapNode;
  depth: number;
  /** Staged (unsaved) edits for this node, if any. */
  pending?: Partial<TaktNodeMapping>;
  onEdit: (nodeId: number, dim: Dim, value: string) => void;
  disabled: boolean;
  /** Predefined section list for the Section dropdown. */
  sectionOptions: string[];
  /** A "hybrid" node — one that carries activities directly AND has child nodes
   *  — is editable here AND collapsible. These props wire its chevron. */
  collapsible?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
}

/** One editable WBS-node row. Section is a real <select> from a predefined list
 *  (picking one re-files the node into that section); A / Zone(B) / Level(C) are
 *  free-text inputs. Edits stage into the parent's pending buffer; the tab's
 *  Save commits them. An on-board count shows fully-tagged / direct tasks. */
const LeafRow: React.FC<Props> = ({
  node, depth, pending, onEdit, disabled, sectionOptions,
  collapsible, collapsed, onToggle,
}) => {
  const read = (dim: Dim) => {
    const committed = node.mapping[dim];
    const staged = pending && dim in pending;
    return {
      val: staged ? (pending![dim] as string) : committed,
      dirty: staged && pending![dim] !== committed,
    };
  };

  const sectionCell = () => {
    const { val, dirty } = read("section_name");
    // Keep the node's current section pickable even if it's not in the list.
    const opts = !val || sectionOptions.includes(val) ? sectionOptions : [val, ...sectionOptions];
    return (
      <select
        className={`wbsmap-input wbsmap-select ${dirty ? "is-dirty" : ""}`}
        value={val}
        disabled={disabled}
        onChange={(e) => onEdit(node.id, "section_name", e.target.value)}
      >
        <option value="">—</option>
        {opts.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  };

  const textCell = (dim: Dim) => {
    const { val, dirty } = read(dim);
    return (
      <input
        className={`wbsmap-input ${dirty ? "is-dirty" : ""}`}
        value={val}
        disabled={disabled}
        onChange={(e) => onEdit(node.id, dim, e.target.value)}
      />
    );
  };

  const { tasks, fully_tagged } = node.counts;
  // Row state: amber while it has unsaved edits, green once it carries a section,
  // muted when still unmapped — a quick visual scan of mapping coverage.
  const dirty = Boolean(pending && Object.keys(pending).length > 0);
  const mapped = Boolean(((pending?.section_name ?? node.mapping.section_name) || "").trim());
  const stateClass = dirty ? "is-dirty" : mapped ? "is-mapped" : "is-unmapped";

  return (
    <div className={`wbsmap-leaf ${stateClass}`}>
      <span
        className="wbsmap-leaf-name"
        style={{ paddingLeft: depth * 18 + 12 }}
        title={node.name}
      >
        {collapsible && (
          <span
            className="wbsmap-chevron"
            role="button"
            tabIndex={0}
            onClick={onToggle}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onToggle?.();
              }
            }}
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? "▸" : "▾"}
          </span>
        )}
        {node.name}
      </span>
      {sectionCell()}
      {textCell("takt_a")}
      {textCell("takt_b")}
      {textCell("takt_c")}
      <span
        className="wbsmap-leaf-counts"
        title="activities on the board (fully tagged) / total tasks in this node"
      >
        {tasks > 0 ? (
          <>
            <span className="wbsmap-on">{fully_tagged}</span>
            <span className="wbsmap-dim">/{tasks}</span>
          </>
        ) : (
          <span className="wbsmap-dim">—</span>
        )}
      </span>
    </div>
  );
};

export default LeafRow;
